/**
 * Referral API Service
 * 레퍼럴 통계, 출금 관련 API 호출
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

/**
 * 레퍼럴 통계 타입
 */
export interface ReferralStats {
  totalEarnings: number;
  availableBalance: number;
  pendingWithdrawals: number;
  completedWithdrawals: number;
  referredUsersCount: number;
  referredUsers: Array<{
    uid: string;
    displayName: string;
    email: string;
    memberGrade: string;
    joinedAt: Date;
    totalPayments: number;
  }>;
}

/**
 * 레퍼럴 크레딧 내역 타입
 */
export interface ReferralCredit {
  creditId: string;
  referrerUid: string;
  referredUid: string;
  paymentId: string;
  amount: number;
  percentage: number;
  productType: string;
  productName: string;
  originalAmount: number;
  status: string;
  createdAt: Date;
}

/**
 * 출금 요청 타입
 */
export interface WithdrawalRequest {
  withdrawalId: string;
  uid: string;
  userEmail: string;
  userName: string;
  amount: number;
  bankAccount: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  requestedAt: Date;
  processedAt?: Date;
  processedBy?: string;
  note?: string;
}

/**
 * 레퍼럴 통계 조회
 */
export const getReferralStats = async (uid: string): Promise<ReferralStats> => {
  try {
    const getReferralStatsFunc = httpsCallable(functions, 'getReferralStats');
    const result = await getReferralStatsFunc({ uid });

    const data = (result.data as any).data;

    console.log('[getReferralStats] Raw data:', data);
    console.log('[getReferralStats] availableBalance:', data.availableBalance, typeof data.availableBalance);
    console.log('[getReferralStats] referredUsers:', data.referredUsers);

    // Firestore Timestamp를 Date로 변환
    return {
      ...data,
      referredUsers: data.referredUsers.map((user: any) => ({
        ...user,
        joinedAt: user.joinedAt?._seconds
          ? new Date(user.joinedAt._seconds * 1000)
          : new Date(user.joinedAt)
      }))
    };
  } catch (error: any) {
    console.error('[getReferralStats] Error:', error);
    throw new Error(error.message || '레퍼럴 통계 조회에 실패했습니다.');
  }
};

/**
 * 출금 요청
 */
export const requestWithdrawal = async (
  amount: number,
  bankAccount: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
  }
): Promise<string> => {
  try {
    const requestWithdrawalFunc = httpsCallable(functions, 'requestWithdrawal');
    const result = await requestWithdrawalFunc({ amount, bankAccount });

    const data = (result.data as any).data;
    return data.withdrawalId;
  } catch (error: any) {
    console.error('[requestWithdrawal] Error:', error);
    throw new Error(error.message || '출금 요청에 실패했습니다.');
  }
};

/**
 * 출금 요청 목록 조회 (관리자)
 */
export const getWithdrawalRequests = async (
  status?: string,
  limit?: number
): Promise<WithdrawalRequest[]> => {
  try {
    const getWithdrawalRequestsFunc = httpsCallable(functions, 'getWithdrawalRequests');
    const result = await getWithdrawalRequestsFunc({ status, limit });

    const data = (result.data as any).data;

    // Firestore Timestamp를 Date로 변환
    return data.map((withdrawal: any) => ({
      ...withdrawal,
      requestedAt: withdrawal.requestedAt?._seconds
        ? new Date(withdrawal.requestedAt._seconds * 1000)
        : new Date(withdrawal.requestedAt),
      processedAt: withdrawal.processedAt?._seconds
        ? new Date(withdrawal.processedAt._seconds * 1000)
        : withdrawal.processedAt
        ? new Date(withdrawal.processedAt)
        : undefined
    }));
  } catch (error: any) {
    console.error('[getWithdrawalRequests] Error:', error);
    throw new Error(error.message || '출금 요청 목록 조회에 실패했습니다.');
  }
};

/**
 * 출금 승인 (관리자)
 */
export const approveWithdrawal = async (
  withdrawalId: string,
  note?: string
): Promise<void> => {
  try {
    const approveWithdrawalFunc = httpsCallable(functions, 'approveWithdrawal');
    await approveWithdrawalFunc({ withdrawalId, note });
  } catch (error: any) {
    console.error('[approveWithdrawal] Error:', error);
    throw new Error(error.message || '출금 승인에 실패했습니다.');
  }
};

/**
 * 출금 요청 일괄 승인 (관리자)
 * 모든 pending 상태의 출금 요청을 한 번에 완료 처리
 */
export const bulkApproveWithdrawals = async (): Promise<{
  processed: number;
  succeeded: number;
  failed: number;
  results: Array<{ withdrawalId: string; status: string; message?: string }>;
}> => {
  try {
    const bulkApproveWithdrawalsFunc = httpsCallable(functions, 'bulkApproveWithdrawals');
    const result = await bulkApproveWithdrawalsFunc({});

    const data = (result.data as any).data;
    return data;
  } catch (error: any) {
    console.error('[bulkApproveWithdrawals] Error:', error);
    throw new Error(error.message || '일괄 출금 승인에 실패했습니다.');
  }
};
